<script setup lang="ts">
//#region imports
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { cloneDeep, isEmpty } from 'lodash-es';
import numeral from 'numeral';
import { useApi } from '@/api';
import { usePopupManager } from '@/components/PopupManager.vue';
import { Tree, TreeBranch } from '@/views/CRM/components/Tree';
import UiInputWrapper from '@/views/CRM/components/UiInputWrapper.vue';
import CrmTag from '@/views/CRM/components/CrmTag.vue';
import ActivityCheckControl from '@/views/CRM/Activities/components/ActivityCheckControl.vue';
import ActivityActionsLanguages from '@/views/CRM/Activities/components/ActivityActionsLanguages.vue';
import CrmErrorPanel from '@/views/CRM/components/CrmErrorPanel.vue';
import { saveBlob } from '@/utils/fileSaver';
import { useError } from '@/composables/useError';
import { getCountryCodeByLanguage } from '@/helpers/language';
import { getLiter } from '@/views/CRM/helpers/getLiter';
import { useActivityActionsPolicy } from '@/views/CRM/Activities/composables/activityActionsPolicy';

import type { CrmActivity, CrmActionGroup, CrmAction, CrmTemplate } from '@/types/crm';
//#endregion

//#region types
type TemplateId = CrmTemplate['id'];

interface ActivityRange {
  stats_period_start?: string | null;
  stats_period_end?: string | null;
}

interface Props {
  loading?: boolean;
  activityRange?: ActivityRange | null;
  canReview?: boolean;
  state?: string;
  activity?: CrmActivity;
  templates?: CrmTemplate[];
  controlGroupActive?: boolean;
  controlGroupPercent?: number | string;
  controlGroupPlayers?: number;
  reworkState?: CrmActivity;
  currentBrand?: string;
  errors?: Record<string, any>;
}

interface Emits {
  'update:controlGroupActive': [value: boolean];
  updateShadowedGroups: [value: any[]];
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  activityRange: null,
  canReview: false,
  state: 'edit',
  activity: () => ({} as CrmActivity),
  templates: () => [],
  controlGroupActive: true,
  controlGroupPercent: 20,
  controlGroupPlayers: 0,
  reworkState: () => ({} as CrmActivity),
  currentBrand: '',
  errors: () => ({}),
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const { t } = useI18n();
const api = useApi();
const popupManager = usePopupManager();
//#endregion

//#region state
const shadowKey = ref(-1);
const oldShadowKey = ref(-1);
const isVisibleStatistic = ref(false);
//#endregion

//#region computed
const activity = computed(() => props.activity || {});
const state = computed(() => props.state || 'edit');
const errors = computed(() => props.errors || {});
const templates = computed(() => props.templates || []);
const reworkState = computed(() => props.reworkState || {});
const controlGroupActive = computed(() => props.controlGroupActive);
const canReview = computed(() => props.canReview);
const { getError } = useError({ errors });

const { modes, permissions } = useActivityActionsPolicy({
  activity,
  state,
  controlGroupActive,
  canReview,
});

const indexShift = computed(() => (modes.value.isHasControlGroup ? 1 : 0));

const visibleChangeStatistic = (val: boolean) => {
  isVisibleStatistic.value = val;
};

const getStatsWithPercent = (num: string | number, percent: string | number) => {
  if (num && percent) {
    return `${num} / ${percent}`;
  }

  return 0;
};

const isDisplayPlayers = computed(() => {
  if (activity.value.id) {
    return activity.value.is_players_attached;
  }

  return true;
});

const actionGroupsWithoutControl = computed<CrmActionGroup[]>(() => {
  const actionGroups = activity.value.action_groups || [];
  return actionGroups.filter((actionGroup) => !actionGroup.is_control);
});

const actionGroupsMeta = computed(() => {
  return actionGroupsWithoutControl.value.map((actionGroup) => {
    const meta: Record<string, any> = {
      list: [],
    };
    const actionGroupActions = actionGroup.actions || [];
    const emailActions = actionGroupActions.filter((action) => action.type === 'email');
    if (emailActions.length) {
      let languages = actionGroupActions.map((action) => ({
        name: action.name,
        is_default: action.is_default,
        languageCode: action.language_code,
        email_template_id: action.email_template_id,
        countryCode: getCountryCodeByLanguage(action.language_code),
      }));

      const stats = {
        title: t('crm.activities.actions.email_statistics.title'),
        values: [
          {
            name: t('crm.activities.actions.email_statistics.playersCount'),
            value: actionGroup?.stats?.players_count || 0,
          },
          {
            name: t('crm.activities.actions.email_statistics.acceptedCount'),
            value: actionGroup?.stats?.email_accepted_count || 0,
          },
          {
            name: t('crm.activities.actions.email_statistics.sent'),
            value: actionGroup?.stats?.email_send_count || 0,
          },
          {
            name: t('crm.activities.actions.email_statistics.bounced'),
            value: actionGroup?.stats?.email_bounced_count || 0,
          },
          {
            name: t('crm.activities.actions.email_statistics.opened'),
            value: getStatsWithPercent(
              actionGroup?.stats?.email_view_count || 0,
              actionGroup?.stats?.email_view_percent || 0,
            ) || 0,
          },
          {
            name: t('crm.activities.actions.email_statistics.uniqueClicks'),
            value: getStatsWithPercent(
              actionGroup?.stats?.email_click_count || 0,
              actionGroup?.stats?.email_click_percent || 0,
            ) || 0,
          },
        ],
      };

      const defaultItem = languages.find((item) => item.is_default);
      languages = languages.filter((item) => !item.is_default);

      if (defaultItem) {
        languages.unshift(defaultItem);
      }

      const languagesBase = languages.slice(0, 3);
      const languagesRest = languages.slice(3);

      meta.email = {
        type: 'email',
        name: (defaultItem?.name || '').trim(),
        languages: {
          full: languages,
          base: languagesBase,
          rest: languagesRest,
        },
        stats,
      };

      meta.list.push(meta.email);
    }

    const smsActions = actionGroupActions.filter((action) => action.type === 'sms');

    if (smsActions.length) {
      let languages = actionGroupActions.map((action) => ({
        name: action.name,
        is_default: action.is_default,
        languageCode: action.language_code,
        countryCode: getCountryCodeByLanguage(action.language_code),
      }));

      const stats = {
        title: t('crm.activities.actions.sms_statistics.title'),
        values: [
          {
            name: t('crm.activities.actions.sms_statistics.playersCount'),
            value: actionGroup?.stats?.players_count || 0,
          },
          {
            name: t('crm.activities.actions.sms_statistics.acceptedCount'),
            value: actionGroup?.stats?.sms_accepted_count || 0,
          },
          {
            name: t('crm.activities.actions.sms_statistics.sent'),
            value: getStatsWithPercent(
              actionGroup?.stats?.sms_sent_count || 0,
              actionGroup?.stats?.sms_sent_percent || 0,
            ) || 0,
          },
          {
            name: t('crm.activities.actions.sms_statistics.delivered'),
            value: getStatsWithPercent(
              actionGroup?.stats?.sms_delivered_count || 0,
              actionGroup?.stats?.sms_delivered_percent || 0,
            ) || 0,
          },
          {
            name: t('crm.activities.actions.sms_statistics.spent'),
            value: actionGroup?.stats?.price_sum || `$${numeral(0).format('0,0.00')}`,
          },
        ],
      };

      const defaultItem = languages.find((item) => item.is_default);
      languages = languages.filter((item) => !item.is_default);

      if (defaultItem) {
        languages.unshift(defaultItem);
      }

      const languagesBase = languages.slice(0, 3);
      const languagesRest = languages.slice(3);

      meta.sms = {
        type: 'sms',
        name: (defaultItem?.name || '').trim(),
        languages: {
          full: languages,
          base: languagesBase,
          rest: languagesRest,
        },
        stats,
      };

      meta.list.push(meta.sms);
    }

    const callCenterActions = actionGroup.actions.filter((action) => action.type === 'call');
    if (callCenterActions.length) {
      const stats = {
        title: t('crm.activities.actions.call_statistics.title'),
        values: [
          {
            name: t('crm.activities.actions.call_statistics.successfulCalls'),
            value: actionGroup?.stats?.calls_success_count || 0,
            labelClass: 'crm-activity-actions_success',
          },
          {
            name: t('crm.activities.actions.call_statistics.failedCalls'),
            value: actionGroup?.stats?.calls_fail_count || 0,
            labelClass: 'crm-activity-actions_decline',
          },
          {
            name: t('crm.activities.actions.call_statistics.totalCalls'),
            value: actionGroup?.stats?.calls_count || 0,
          },
        ],
      };

      const { call_center_project_id, name } = actionGroup.actions[0] || {};

      meta.call = {
        type: 'call',
        call_center_project_id,
        name,
        stats,
      };

      meta.list.push(meta.call);
    }

    return meta;
  });
});

const sendHint = computed(() => (activity.value.send_actions_repeat
  ? t('crm.activities.actions.send.hint', {
    interval: activity.value.send_actions_repeat_interval,
    count: activity.value.send_actions_count,
  }, +(activity.value.send_actions_repeat_interval || 0))
  : t('crm.activities.actions.send.hint_once')));

const getActionIndexKey = (actionGroupId: string, actionId: string) => `${actionGroupId}_${actionId}`;

const actionIndex = computed(() => {
  const result: Record<string, CrmAction> = {};
  const actionGroups = activity.value.action_groups || [];
  actionGroups.forEach((actionGroup) => {
    const actions = actionGroup.actions || [];
    actions.forEach((action) => {
      const key = getActionIndexKey(actionGroup.id, action.id);
      result[key] = action;
    });
  });
  return result;
});

const getShadowedValue = (key: string | number) => {
  if (key === 'control' && !props.controlGroupActive) {
    return 0;
  }

  const normalizedKey = key === 'control' && props.controlGroupActive
    ? 0
    : key;

  if (normalizedKey === shadowKey.value) {
    const totalWithoutShadow = (activity.value.action_groups || [])
      .filter((item, index: number) => {
        const indexCondition = index !== normalizedKey;
        const controlGroupCondition = (!props.controlGroupActive && !item.is_control) || props.controlGroupActive;
        return indexCondition && controlGroupCondition;
      })
      .reduce((a: number, b) => a + +b.percent, 0);
    return 100 - totalWithoutShadow;
  }

  return (activity.value.action_groups || [])[+normalizedKey]?.percent;
};

const shadowedGroups = computed(() => {
  const actionGroups = activity.value.action_groups || [];
  return actionGroups.map((item, index) => ({
    ...item,
    index,
    percent: getShadowedValue(index),
  }));
});

const errorsActionGroups = computed(() => {
  const result: any = {
    general: [],
    actionGroups: {},
    percent: {},
    hasErrors: false,
  };

  const action_groups = errors.value.action_groups;

  if (Array.isArray(action_groups) && action_groups.length) {
    action_groups.forEach((actionGroupRaw, index) => {
      const actionGroup = (actionGroupRaw || {});
      if (!actionGroup) {
        return true;
      }

      if (actionGroup.message) {
        result.general.push(actionGroup.message);
      }

      if (Array.isArray(actionGroup.actions) && actionGroup.actions.length) {
        actionGroup.actions.forEach((action: Record<string, any>, actionIndexItem: number) => {
          if (!action) {
            return true;
          }

          if (!result.actionGroups[index]) {
            result.actionGroups[index] = {
              paramsList: [],
              actions: {},
              percent: [],
            };
          }

          if (typeof action.code === 'number') {
            result.actionGroups[index].paramsList.push(action.message);
          } else {
            const fields = Object.keys(action);
            fields.forEach((field) => {
              const fieldErrors = action[field];
              if (!Array.isArray(fieldErrors)) {
                return;
              }

              fieldErrors.forEach((actionParamErrorRaw) => {
                const actionParamError = (actionParamErrorRaw || {});
                const { type } = activity.value.action_groups[index]?.actions[actionIndexItem] || {};

                if (!type) {
                  return;
                }

                if (!result.actionGroups[index].actions[type]) {
                  let errorText = t('crm.activities.actions.errors.has_param_error', {
                    entity: type,
                  });

                  if (type === 'call') {
                    errorText = t('crm.activities.actions.errors.has_call_error');
                  }

                  result.actionGroups[index].paramsList.push(errorText);
                  result.actionGroups[index].actions[type] = {};
                }

                if (!result.actionGroups[index].actions[type][actionIndexItem]) {
                  result.actionGroups[index].actions[type][actionIndexItem] = {};
                }
                result.actionGroups[index].actions[type][actionIndexItem][field] = actionParamError.message;
              });
            });
          }

          return true;
        });
      }

      if (Array.isArray(actionGroup.percent) && actionGroup.percent.length) {
        if (!result.actionGroups[index]) {
          result.actionGroups[index] = {
            paramsList: [],
            actions: {},
            percent: [],
          };
        }

        actionGroup.percent.forEach((percentErrorRaw: unknown) => {
          const percentError = (percentErrorRaw || {}) as Record<string, any>;
          result.actionGroups[index].percent.push(percentError.message);
        });
      }

      return true;
    });
  }

  if (result.general.length || !isEmpty(result.actionGroups) || !isEmpty(result.percent)) {
    result.hasErrors = true;
  }

  return result;
});

const hasErrors = computed(() => !!(errorsActionGroups.value.hasErrors
  || getError('send_actions_repeat_interval')
  || getError('send_actions_count')));

const isApprovedActionsModel = computed<boolean>({
  get: () => !!reworkState.value.is_approved_actions,
  set: (value) => {
    reworkState.value.is_approved_actions = value;
  },
});
//#endregion

//#region methods
function isPreviewAvailable(type: string) {
  return props.state !== 'pause-edit' && ['email', 'sms'].includes(type);
}

function isEditAvailable(type: string) {
  return permissions.value.canEditAction({} as CrmActionGroup, type);
}

function isActionsSendAvailable(type: string) {
  return ['sms', 'email'].includes(type);
}

function isStatisticAvailable(type: string) {
  return ['sms', 'call', 'email'].includes(type);
}

async function exportData(index: number) {
  const actionGroupId = activity.value.action_groups?.[index]?.id;
  if (!actionGroupId) return;

  const file = await api.getActionGroupsPlayers(actionGroupId);
  const linkBlob = new Blob(
    [file.data],
    { type: file.headers['content-type'] },
  );

  saveBlob(linkBlob, activity.value.name || '');
}

function isActionsSendDisabled(actions: CrmAction[]) {
  return actions.every((action) => !action.message.trim());
}

function getActionGroupsErrorList(index: number, field: string) {
  return (errorsActionGroups.value.actionGroups[index] && errorsActionGroups.value.actionGroups[index][field]) || [];
}

function getActionGroupsErrorNotEmpty(index: number, field: string) {
  const list = getActionGroupsErrorList(index, field);
  return list.length > 0
    ? list[0]
    : undefined;
}

function saveState() {
  oldShadowKey.value = shadowKey.value;
}

function restoreState() {
  shadowKey.value = oldShadowKey.value;
}

function toggleShadowKey(value: number) {
  shadowKey.value = shadowKey.value !== value
    ? value
    : -1;
}

function getDefaultActionGroup(): Partial<CrmActionGroup> {
  return {
    is_control: false,
    percent: 0,
    actions: [],
  };
}

function setControlGroupActive(value: boolean) {
  if (!permissions.value.canToggleControlGroup) {
    return;
  }

  if (!modes.value.isHasControlGroup && value) {
    const controlGroup = getDefaultActionGroup();
    controlGroup.is_control = true;
    controlGroup.percent = 100;

    if (shadowKey.value === 0) {
      shadowKey.value = 1;
    }

    activity.value.action_groups?.unshift(controlGroup as CrmActionGroup);
  }

  emit('update:controlGroupActive', value);

  if (shadowKey.value === 0 && !value) {
    shadowKey.value = -1;
  }
}

function setPercent(key: number, value: string | number) {
  const normalizedValue = +value > 100
    ? 100
    : +value;
  if (activity.value.action_groups?.[key]) {
    activity.value.action_groups[key].percent = normalizedValue;
  }
}

function handleSendTest(actionGroup: CrmActionGroup, type: 'email' | 'sms', translationIndex = -1) {
  const openMap = {
    email: () => popupManager.confirmSendTestEmail.open({
      data: {
        currentBrand: props.currentBrand,
        actions: confirmSendTestActions,
        selectedActions: confirmSendTestSelectedActions,
      },
    }),
    sms: () => popupManager.confirmSendTestSms.open({
      data: {
        actions: confirmSendTestActions,
        selectedActions: confirmSendTestSelectedActions,
      },
    }),
  };

  const actionsFiltered = (actionGroup.actions as CrmAction[])
    .filter((action) => action.type === type);

  const defaultAction = actionsFiltered.find((action) => action.is_default);

  const confirmSendTestActions = {
    defaultName: (defaultAction?.name || '').trim(),
    actions: actionsFiltered,
  };

  const cb = translationIndex === -1
    ? (item: Record<string, any>) => !!item.message
    : (_item: Record<string, any>, index: number) => index === translationIndex;

  const confirmSendTestSelectedActions = actionsFiltered.map(cb);

  if (type === 'sms') {
    openMap.sms();
    return;
  }

  openMap.email();
}

function handleAddNewActionGroup() {
  activity.value.action_groups.push(getDefaultActionGroup() as CrmActionGroup);
}

function handleSpliceActionGroup(actionGroup: CrmActionGroup, isDelete: boolean) {
  if (!activity.value.action_groups) {
    return;
  }

  const index = activity.value.action_groups.indexOf(actionGroup);
  if (index === shadowKey.value && isDelete) {
    shadowKey.value = -1;
  } else if (index < shadowKey.value) {
    const shift = isDelete ? -1 : 1;
    shadowKey.value = shadowKey.value + shift;
  }

  if (isDelete) {
    activity.value.action_groups.splice(index, 1);
  } else {
    const { id: _groupId, ...clonedGroup } = cloneDeep(actionGroup);

    if (clonedGroup.actions) {
      clonedGroup.actions = clonedGroup.actions.map(({ id: _actionId, ...action }) => action as CrmAction);
    }

    activity.value.action_groups.splice(index + 1, 0, {
      ...clonedGroup,
      percent: 0,
    } as CrmActionGroup);
  }

  if (errors.value && errors.value.action_groups && errors.value.action_groups.length) {
    if (isDelete) {
      errors.value.action_groups.splice(index, 1);
    } else {
      errors.value.action_groups.splice(index + 1, 0, null);
    }
  }
}

function handleDeleteActionGroup(actionGroup: CrmActionGroup) {
  handleSpliceActionGroup(actionGroup, true);
  activity.value.is_approved_actions = false;
}

function handleCloneActionGroup(actionGroup: CrmActionGroup) {
  handleSpliceActionGroup(actionGroup, false);
}

function handleAddAction(actionGroup: CrmActionGroup, type: string, preview = false) {
  handleEditAction(actionGroup, type, preview);
}

function handlePreviewAction(actionGroup: CrmActionGroup, type: string, payload: any = null) {
  handleEditAction(actionGroup, type, true, payload);
}

function handleChangeTemplate(
  { templateId, cb }: { templateId: TemplateId | undefined; cb: (id: TemplateId | undefined) => void },
) {
  popupManager.templateMailAction.open({
    data: {
      templates: templates.value,
      templateId,
      isEdit: true,
    },
    listeners: {
      'select-template': cb,
    },
  });
}

function handleSaveAction(actionGroup: CrmActionGroup | null, actions: CrmAction[], type: string) {
  if (!actionGroup) {
    return;
  }

  actionGroup.actions = (actionGroup.actions || [])
    .filter((action) => action.type !== type)
    .concat(actions);
}

function handleDeleteAction(actionGroup: CrmActionGroup, type: string) {
  actionGroup.actions = actionGroup.actions.filter((action) => action.type !== type);
  actionGroup.is_approved = false;
  activity.value.is_approved_actions = false;
}

function handleEditAction(
  actionGroup: CrmActionGroup,
  type: string,
  preview = false,
  payload: any = null,
  isEdit = false,
) {
  if (!actionGroup.actions) {
    actionGroup.actions = [];
  }

  switch (type) {
  case 'sms':
    if (!preview) {
      popupManager.addSmsAction.open({
        data: {
          activity: activity.value,
          group: actionGroup,
          payload,
        },
        listeners: {
          save: handleSaveAction,
          'send-test-sms': ($event: { actionGroup: CrmActionGroup, index: number }) => handleSendTest($event.actionGroup, 'sms', $event.index),
        },
      });
    } else {
      popupManager.addSmsActionPreview.open({
        data: {
          activity: activity.value,
          group: actionGroup,
          payload,
        },
        listeners: {
          'send-test-sms': ($event: { actionGroup: CrmActionGroup, index: number }) => handleSendTest($event.actionGroup, 'sms', $event.index),
        },
      });
    }
    break;
  case 'email':
    if (!preview) {
      if (isEdit) {
        popupManager.addMailAction.open({
          data: {
            activity: activity.value,
            currentBrand: props.currentBrand,
            templates: templates.value,
            group: actionGroup,
            payload,
          },
          listeners: {
            'send-test-email': ($event: { actionGroup: CrmActionGroup, index: number }) => handleSendTest($event.actionGroup, 'email', $event.index),
            save: handleSaveAction,
            'change-template': handleChangeTemplate,
          },
        });
      } else if (templates.value.length === 1) {
        popupManager.addMailAction.open({
          data: {
            activity: activity.value,
            currentBrand: props.currentBrand,
            templates: templates.value,
            group: actionGroup,
            payload,
            templateId: templates.value[0]?.id,
          },
          listeners: {
            'send-test-email': ($event: { actionGroup: CrmActionGroup, index: number }) => handleSendTest($event.actionGroup, 'email', $event.index),
            save: handleSaveAction,
            'change-template': handleChangeTemplate,
          },
        });
      } else {
        const templateId = templates.value.find(({ is_default }: CrmTemplate) => is_default)?.id;

        popupManager.templateMailAction.open({
          data: {
            templates: templates.value,
            templateId,
            isEdit: false,
          },
          listeners: {
            'select-template': (id: TemplateId | undefined) => {
              popupManager.addMailAction.open({
                data: {
                  activity: activity.value,
                  currentBrand: props.currentBrand,
                  templates: templates.value,
                  group: actionGroup,
                  payload,
                  templateId: id,
                },
                listeners: {
                  'send-test-email': ($event: { actionGroup: CrmActionGroup, index: number }) => handleSendTest($event.actionGroup, 'email', $event.index),
                  save: handleSaveAction,
                  'change-template': handleChangeTemplate,
                },
              });
            },
          },
        });
      }
    } else {
      popupManager.addMailActionPreview.open({
        data: {
          activity: activity.value,
          currentBrand: props.currentBrand,
          group: actionGroup,
          payload,
        },
        listeners: {
          'send-test-email': ($event: { actionGroup: CrmActionGroup, index: number }) => handleSendTest($event.actionGroup, 'email', $event.index),
        },
      });
    }
    break;
  case 'call':
    if (!preview) {
      popupManager.addCallCenterAction.open({
        data: {
          activity: activity.value,
          currentBrand: props.currentBrand,
          group: actionGroup,
          payload,
        },
        listeners: {
          save: handleSaveAction,
        },
      });
    }
    break;
  case 'push':
    popupManager.addPushAction.open({
      data: {
        group: actionGroup,
        payload: preview,
      },
      listeners: {
        save: (savedActionGroup, action) => handleSaveAction(savedActionGroup, [action], 'push'),
      },
    });
    break;
  default:
  }
}

function getCheckActionGroup(actionGroupId: string): CrmActionGroup {
  return reworkState.value.action_groups.find((item: CrmActionGroup) => item.id === actionGroupId)
    || ({
      actions: [],
      is_approved: false,
    } as unknown as CrmActionGroup);
}

function getCheckActionList(actionGroupId: string, type: string) {
  const actionGroup = getCheckActionGroup(actionGroupId);
  return (actionGroup.actions || []).filter((item) => {
    const key = getActionIndexKey(actionGroupId, item.id);
    const action = actionIndex.value[key];
    return action?.type === type;
  });
}

function getCheckAction(actionGroupId: string, type: string) {
  const actions = getCheckActionList(actionGroupId, type);
  return !!actions.length && actions.every((action: CrmAction) => action.is_approved);
}

function setCheckAction(actionGroupId: string, type: string, value: boolean) {
  const actions = getCheckActionList(actionGroupId, type);
  actions.forEach((item: CrmAction) => {
    item.is_approved = value;
  });
}
//#endregion

//#region watch
watch(shadowedGroups, (value) => {
  emit('updateShadowedGroups', value);
}, { immediate: true });
//#endregion

//#region hooks
defineExpose({
  saveState,
  restoreState,
});
//#endregion
</script>

<template>
  <FormRow
    class="crm-activity-actions"
    :error="hasErrors"
  >
    <div class="t:space-y-16">
      <div class="t:flex t:justify-between">
        <UiLabel
          :label="$t('crm.activities.actions.title')"
          :font-size="20"
          bold
        />
        <div
          v-if="modes.isActionsEditState"
          class="t:flex t:items-center t:space-x-16"
        >
          <div class="t:flex t:items-center t:space-x-8">
            <UiLabel
              :label="$t('crm.activities.actions.send.repeat')"
              :font-size="16"
              bold
            />
            <UiSwitch
              v-model="activity.send_actions_repeat"
              :disabled="modes.isStructureLocked"
            />
          </div>
          <UiSeparator
            type="vertical"
          />
          <div class="t:flex t:items-center t:space-x-8">
            <UiInput
              v-model="activity.send_actions_count"
              width="68px"
              :input-mask="'######'"
              :error="getError('send_actions_count')"
              :disabled="!activity.send_actions_repeat || modes.isStructureLocked"
              error-tooltip
            />
            <UiLabel
              :label="$t('crm.activities.actions.send.label_2')"
            />
            <UiInput
              v-model="activity.send_actions_repeat_interval"
              width="68px"
              :input-mask="'######'"
              :error="getError('send_actions_repeat_interval')"
              :disabled="!activity.send_actions_repeat || modes.isStructureLocked"
              error-tooltip
            />
            <UiLabel
              :label="$t('crm.activities.actions.send.day')"
            />
          </div>
          <UiButton
            icon="plus"
            :transparent-background="true"
            :disabled="modes.isStructureLocked"
            @click="handleAddNewActionGroup"
          >
            {{ $t('crm.activities.actions.new_action_group_button') }}
          </UiButton>
        </div>
        <Panel v-if="state === 'approved' || state === 'preview' || state === 'preview-edit'">
          <div
            class="t:flex t:items-center t:space-x-8"
          >
            <UiIcon
              name="repeat"
              :size="12"
              color="var(--text-body-secondary)"
            />
            <UiLabel
              :label="sendHint"
              :font-size="12"
            />
          </div>
        </Panel>
      </div>
      <Panel v-if="modes.isReviewState">
        <template v-slot:prepend>
          <ActivityCheckControl
            v-if="modes.isActionsCheckControlVisible"
            v-model="isApprovedActionsModel"
            :can-edit="modes.isCheckControlEditable"
          />
        </template>
        <div
          class="t:inline-flex t:items-center t:space-x-8"
        >
          <UiIcon
            name="repeat"
            :size="12"
            color="var(--text-body-secondary)"
          />
          <UiLabel
            :label="sendHint"
            :font-size="12"
          />
        </div>
      </Panel>
      <Panel v-if="modes.isControlGroupPanelVisible">
        <template v-slot:prepend>
          <ActivityCheckControl
            v-if="modes.isActionsCheckControlVisible"
            v-model="getCheckActionGroup(activity.action_groups[0]?.id || '').is_approved"
            :can-edit="modes.isCheckControlEditable"
          />
        </template>
        <div
          v-if="modes.isActionsEditState"
          class="t:flex t:items-center t:space-x-8"
        >
          <span>
            {{ $t('crm.activities.actions.control_group') }}
          </span>
          <UiSwitch
            :model-value="controlGroupActive"
            :disabled="!permissions.canToggleControlGroup"
            @update:model-value="setControlGroupActive($event)"
          />
          <UiInputWrapper
            :disabled="!controlGroupActive || modes.isStructureLocked"
          >
            <template v-slot:prepend>
              <span>%</span>
            </template>

            <UiInput
              :model-value="getShadowedValue('control')"
              width="100px"
              :disabled="!controlGroupActive || shadowKey === 0 || modes.isStructureLocked"
              :input-mask="(!controlGroupActive || shadowKey === 0) ? '' : '###'"
              :is-error="!!getActionGroupsErrorNotEmpty(0, 'percent')"
              @update:model-value="setPercent(0, $event)"
            />
            <template v-slot:append>
              <span>
                <ActionIcon
                  :is-overlay="false"
                  :tooltip="$t('crm.tooltips.shadow_percent')"
                  icon="angle-double-down"
                  :color="shadowKey === 0 ? 'var(--text-primary)' : 'var(--text-negative)'"
                  :disabled="!controlGroupActive || modes.isStructureLocked"
                  @click="toggleShadowKey(0)"
                />
              </span>
            </template>
          </UiInputWrapper>
          <span class="crm-activity-actions__of-segment">
            {{ $t('crm.activities.actions.of_segment') }}
          </span>
          <div
            v-if="getActionGroupsErrorNotEmpty(0, 'percent')"
            class="t:flex t:items-start t:ml-4"
          >
            <div class="t:flex t:flex-col t:space-y-8">
              <CrmErrorPanel
                v-for="(error, errorIndex) in getActionGroupsErrorList(0, 'percent')"
                :key="errorIndex"
                class="t:ai-baseline"
              >
                {{ error }}
              </CrmErrorPanel>
            </div>
          </div>
        </div>
        <div
          v-else
          class="t:justify-between t:flex t:items-center t:flex-1"
        >
          <div class="t:flex t:items-center t:space-x-8">
            <div class="crm-activity-actions__percent">
              {{ controlGroupPercent }}%
            </div>
            <span>
              {{ $t('crm.activities.actions.control_group') }}
            </span>
            <UiSeparator type="dot" />
            <CrmTag
              preset="contrast"
              icon="users"
            >
              <div
                v-loading="!isDisplayPlayers"
                class="crm-activity-actions__loading"
              >
                <span v-if="isDisplayPlayers">{{ `${parseFloat(String(controlGroupPlayers || 0)).toLocaleString('en-US')}` }}</span>
              </div>
            </CrmTag>
          </div>
          <ActionIcon
            v-if="activity.moderation_status === 'approved'"
            :tooltip="$t('crm.tooltips.download')"
            icon="down"
            color="var(--text-primary)"
            @click="exportData(0)"
          />
        </div>
      </Panel>
      <div
        v-for="(group, index) in actionGroupsWithoutControl"
        :key="index"
      >
        <Tree
          class="crm-activity-actions__tree"
          thickness="thin"
        >
          <Panel>
            <template v-slot:prepend>
              <ActivityCheckControl
                v-if="modes.isActionsCheckControlVisible"
                v-model="getCheckActionGroup(group.id).is_approved"
                :can-edit="modes.isCheckControlEditable"
              />
            </template>
            <div class="t:flex t:items-center t:justify-between t:flex-1">
              <div class="t:flex t:items-center t:space-x-8 ">
                <template v-if="modes.isActionsEditState">
                  <UiLabel
                    :label="`${$t('crm.activities.actions.action_group')} ${getLiter(+index)}`"
                    :font-size="16"
                  />
                  <UiInputWrapper
                    :disabled="modes.isStructureLocked"
                  >
                    <template v-slot:prepend>
                      <span>%</span>
                    </template>

                    <UiInput
                      :model-value="getShadowedValue(+index + indexShift)"
                      width="100px"
                      :disabled="modes.isStructureLocked || shadowKey === +index + indexShift"
                      :input-mask="(shadowKey === +index + indexShift) ? '' : '###'"
                      :is-error="!!getActionGroupsErrorNotEmpty(+index + indexShift, 'percent')"
                      @update:model-value="setPercent(+index + indexShift, $event)"
                    />
                    <template v-slot:append>
                      <span>
                        <ActionIcon
                          :is-overlay="false"
                          :tooltip="$t('crm.tooltips.shadow_percent')"
                          icon="angle-double-down"
                          :color="shadowKey === +index + indexShift ? 'var(--text-primary)' : 'var(--text-negative)'"
                          :disabled="modes.isStructureLocked"
                          @click="toggleShadowKey(+index + indexShift)"
                        />
                      </span>
                    </template>
                  </UiInputWrapper>
                  <span class="crm-activity-actions__of-segment">
                    {{ $t('crm.activities.actions.of_segment') }}
                  </span>
                  <div
                    v-if="getActionGroupsErrorNotEmpty(+index + indexShift, 'percent')"
                    class="t:flex t:items-start t:ml-4"
                  >
                    <div class="t:flex t:flex-col t:space-y-8">
                      <CrmErrorPanel
                        v-for="(error, errorIndex) in getActionGroupsErrorList(+index + indexShift, 'percent')"
                        :key="errorIndex"
                        class="t:ai-baseline"
                      >
                        {{ error }}
                      </CrmErrorPanel>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="crm-activity-actions__percent">
                    {{ group.percent }}%
                  </div>
                  <UiLabel
                    :label="`${$t('crm.activities.actions.action_group')} ${getLiter(+index)}`"
                    :font-size="16"
                  />
                  <UiSeparator type="dot" />
                  <CrmTag
                    preset="contrast"
                    icon="users"
                  >
                    <div
                      v-loading="!isDisplayPlayers"
                      class="crm-activity-actions__loading"
                    >
                      <span v-if="isDisplayPlayers">{{ `${parseFloat(String(group.players_count || 0)).toLocaleString('en-US')}` }}</span>
                    </div>
                  </CrmTag>
                </template>
              </div>
              <div class="t:flex t:items-center t:-my-2">
                <template v-if="modes.isActionsEditState">
                  <ActionIcon
                    :tooltip="$t('crm.tooltips.clone')"
                    icon="clone"
                    :disabled="modes.isStructureLocked"
                    @click="handleCloneActionGroup(group)"
                  />
                  <ActionIcon
                    :tooltip="$t('crm.tooltips.delete')"
                    icon="trash-alt"
                    color="var(--text-negative)"
                    :disabled="!permissions.canDeleteActionGroup(group)"
                    @click="handleDeleteActionGroup(group)"
                  />
                </template>
                <template v-else>
                  <ActionIcon
                    v-if="activity.moderation_status === 'approved'"
                    :tooltip="$t('crm.tooltips.download')"
                    icon="down"
                    @click="exportData(index + 1)"
                  />
                </template>
              </div>
            </div>
          </Panel>
          <TreeBranch
            v-for="(action, actionInd) in actionGroupsMeta[+index]?.list"
            :key="actionInd"
            class="t:pt-8 t:px-16"
          >
            <Panel>
              <template v-slot:prepend>
                <ActivityCheckControl
                  v-if="modes.isActionsCheckControlVisible"
                  :model-value="getCheckAction(group.id, action.type || '')"
                  :can-edit="modes.isCheckControlEditable"
                  @update:model-value="setCheckAction(group.id, action.type || '', $event)"
                />
              </template>
              <div class="t:flex t:items-center t:justify-between t:flex-1">
                <div class="t:flex t:items-center t:space-x-8 ">
                  <UiLabel
                    :label="action.name || $t('crm.activities.actions.empty_name')"
                  />
                  <UiSeparator type="dot" />
                  <CrmTag :preset="action.type && `notify-${action.type}`" />
                  <template v-if="action.languages?.full.length > 0">
                    <UiSeparator type="dot" />
                    <ActivityActionsLanguages
                      :meta="action.languages"
                    />
                  </template>
                </div>
                <div class="t:flex t:items-center t:-my-2">
                  <template v-if="modes.isFullEditMode">
                    <ActionIcon
                      :tooltip="$t('crm.tooltips.edit')"
                      icon="pen"
                      :disabled="!permissions.canEditAction(group, action.type)"
                      @click="handleEditAction(group, action.type, false, {
                        errors: errorsActionGroups.actionGroups[+index + indexShift]?.actions
                      }, true)"
                    />
                    <ActionIcon
                      v-if="isActionsSendAvailable(action.type)"
                      :tooltip="$t(`crm.tooltips.send_test_${action.type}`)"
                      icon="paper-plane"
                      :disabled="isActionsSendDisabled(group.actions)"
                      @click="handleSendTest(group, action.type)"
                    />
                    <ActionIcon
                      :tooltip="$t('crm.tooltips.delete')"
                      icon="trash-alt"
                      color="var(--text-negative)"
                      :disabled="!permissions.canDeleteAction(group, action.type)"
                      @click="handleDeleteAction(group, action.type)"
                    />
                  </template>
                  <template v-else>
                    <ElDropdown
                      v-if="isStatisticAvailable(action.type)"
                      trigger="click"
                      placement="top"
                      popper-class="crm-activity-actions__popper"
                      @visible-change="visibleChangeStatistic"
                    >
                      <div class="value">
                        <ActionIcon
                          icon="chart-bar"
                          :tooltip="!isVisibleStatistic ? $t('crm.segments.stats.statistics') : ''"
                        />
                      </div>
                      <template v-slot:dropdown>
                        <ElDropdownMenu class="crm-activity-actions__statistics-menu">
                          <div
                            v-if="activityRange"
                            class="t:flex t:mb-8"
                          >
                            <UiIcon
                              name="calendar-days"
                              color="#7F7F7F"
                              :size="11"
                            />
                            <span class="t:ml-4">{{ `${$filters.formatDate4(activityRange?.stats_period_start || '')} - ${$filters.formatDate4(activityRange?.stats_period_end || '')}` }}</span>
                          </div>
                          <CardBlock
                            :title="action.stats.title"
                            :list="action.stats.values"
                          />
                        </ElDropdownMenu>
                      </template>
                    </ElDropdown>
                    <ActionIcon
                      v-if="isActionsSendAvailable(action.type)"
                      :tooltip="$t(`crm.tooltips.send_test_${action.type}`)"
                      icon="paper-plane"
                      :disabled="isActionsSendDisabled(group.actions)"
                      @click="handleSendTest(group, action.type)"
                    />
                    <ActionIcon
                      v-if="isPreviewAvailable(action.type)"
                      :tooltip="$t('crm.tooltips.preview')"
                      icon="eye"
                      @click="handlePreviewAction(group, action.type)"
                    />
                    <ActionIcon
                      v-if="isEditAvailable(action.type)"
                      :tooltip="$t('crm.tooltips.edit')"
                      icon="pen"
                      @click="handleEditAction(group, action.type, false, {
                        errors: errorsActionGroups.actionGroups[+index + indexShift]?.actions
                      }, true)"
                    />
                  </template>
                </div>
              </div>
            </Panel>
          </TreeBranch>
          <TreeBranch
            v-if="modes.isFullEditMode && !actionGroupsMeta[+index]?.list.length"
            class="t:pt-8"
          >
            <div class="crm-activity-actions__add-action-branch">
              <span class="crm-activity-actions__add-action-branch-name">
                {{ $t('crm.activities.actions.add_action') }}
              </span>
              <CrmTag
                v-if="!actionGroupsMeta[+index]?.isHaveSms"
                size="large"
                preset="notify-sms"
                clickable
                @click="handleAddAction(group, 'sms')"
              />
              <CrmTag
                v-if="!actionGroupsMeta[+index]?.email"
                size="large"
                preset="notify-email"
                clickable
                @click="handleAddAction(group, 'email')"
              />
              <CrmTag
                v-if="!actionGroupsMeta[+index]?.call"
                size="large"
                preset="notify-call"
                clickable
                @click="handleAddAction(group, 'call')"
              />
            </div>
          </TreeBranch>
        </Tree>
        <div
          v-if="getActionGroupsErrorNotEmpty(+index + indexShift, 'paramsList')"
          class="t:flex t:flex-col t:items-start t:ml-16"
          :class="{
            't:mt-8': actionGroupsMeta[+index]?.list.length,
            't:mt-12': !actionGroupsMeta[+index]?.list.length,
          }"
        >
          <div class="t:flex t:flex-col t:space-y-8">
            <CrmErrorPanel
              v-for="(error, errorIndex) in getActionGroupsErrorList(+index + indexShift, 'paramsList')"
              :key="errorIndex"
            >
              {{ error }}
            </CrmErrorPanel>
          </div>
        </div>
      </div>
      <div
        v-if="errorsActionGroups.general.length"
        class="t:flex t:flex-col t:items-start t:mt-12 t:space-x-8"
      >
        <UiSeparator
          class="t:mb-4"
        />
        <CrmErrorPanel
          v-for="(error, index) in errorsActionGroups.general"
          :key="index"
        >
          {{ error }}
        </CrmErrorPanel>
      </div>
    </div>
  </FormRow>
</template>

<style lang="scss">
.crm-activity-actions {
  &.form-row {
    padding: 16px 24px;
  }
  &__percent {
    padding: 4px 8px;
    border-radius: 8px;
    background: var(--background-positive-subtle);
    color: var(--text-positive);
    font-size: 14px;
  }
  &__loading {
    height: 100%;
    min-width: 16px;
    z-index: 9;
    .el-loading-spinner {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      margin-top: 0;
      transform: translateY(-50%);

      .circular {
        height: 100%;
        width: 10px;
        .path {
          stroke: var(--text-positive);
        }
      }
    }

  }
  &__tree {
   padding-left: 0;
  }
  &__of-segment {
    color: var(--text-body-secondary);
    font-size: 14px;
  }
  &__action-name{
    font-size: 14px;
  }
  &__popper.el-popper.el-dropdown__popper {
    box-shadow: 0 4px 32px 0 rgba(0, 0, 0, 0.16);
    border: none;
  }
  &__statistics-menu.el-dropdown-menu {
    border-radius: 8px;
    background: var(--background-body);
    padding: 16px;
    width: 320px;
    max-height: 400px;
  }
  &__add-action-branch {
    display: inline-flex;
    height: 40px;
    padding: 0 16px;
    align-items: center;
    gap: 8px;
    border-radius: 8px;
    border: 1px dashed var(--border-focused);
  }
  &__add-action-branch-name {
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 700;
    line-height: 100%; /* 14px */
  }
  &_success {
    color: var(--text-positive);
  }
  &_decline {
    color: var(--text-negative);
  }
}
</style>
